import api from '@/api/api';

const state = {
    targetBuilds: [],
    failedTestCases: [],
    branches: [],
    ciServices: [],
    products: [],
    testRunsTargetBuilds: [],
    testRunsTargetBuildsTotalCount: 0,
    currentMtrSummary: null,
    currentTargetBuild: null,
    history: [],
    historyTotalCount: 0,
    buildTypes: [],
    additionalTools: [],
    failedTestResultsLogsDetails: {},
};

const getters = {
    targetBuilds: state => state.targetBuilds,
    buildTypes: state => state.buildTypes,
    ciServices: state => state.ciServices,
    additionalTools: state => state.additionalTools,
    failedTestCases: state => state.failedTestCases,
    targetBuildHistory: state => state.history,
    targetBuildHistoryTotalCount: state => state.historyTotalCount,
    products: state => state.products,
    currentMtrSummary: state => state.currentMtrSummary,
    currentTargetBuild: state => state.currentTargetBuild,
    testRunsTargetBuilds: state => state.testRunsTargetBuilds,
    testRunsTargetBuildsTotalCount: state => state.testRunsTargetBuildsTotalCount,
    branches: state => state.branches,
    targetBuildById: state => id => {
        return state.targetBuilds.find(item => item.id.toString() === id.toString());
    },
    failedTestResultsLogsDetails: state => state.failedTestResultsLogsDetails,
};

const mutations = {
    SET_TARGET_BUILDS(state, targetBuilds) {
        state.targetBuilds = targetBuilds;
    },
    SET_BUILD_TYPES(state, buildTypes) {
        state.buildTypes = buildTypes;
    },
    SET_ADDITIONAL_TOOLS(state, additionalTools) {
        state.additionalTools = additionalTools;
    },
    SET_TARGET_BUILD_HISTORY(state, targetBuilds) {
        state.history = targetBuilds;
    },
    SET_TARGET_BUILD_HISTORY_TOTAL_COUNT(state, count) {
        state.historyTotalCount = count;
    },
    CLEAR_TARGET_BUILD_HISTORY(state) {
        state.history = [];
    },
    SET_BRANCHES(state, branches) {
        state.branches = branches;
    },
    SET_CI_SERVICES(state, ciServices) {
        state.ciServices = ciServices;
    },
    SET_TEST_RUNS_TARGET_BUILDS(state, testRunsTargetBuilds) {
        state.testRunsTargetBuilds = testRunsTargetBuilds;
    },
    SET_TEST_RUNS_TARGET_BUILDS_TOTAL_COUNT(state, testRunsTargetBuildsTotalCount) {
        state.testRunsTargetBuildsTotalCount = parseInt(testRunsTargetBuildsTotalCount);
    },
    CLEAR_TEST_RUNS_TARGET_BUILDS(state) {
        state.testRunsTargetBuilds = [];
        state.testRunsTargetBuildsTotalCount = 0;
    },
    ADD_TARGET_BUILD(state, targetBuild) {
        if (state.targetBuilds.findIndex(item => item.id === targetBuild.id) !== -1) return;
        state.targetBuilds.push(targetBuild);
    },
    SET_CURRENT_MTR_SUMMARY(state, mtrSummary) {
        state.currentMtrSummary = mtrSummary;
        state.failedTestResultsLogsDetails = {};
    },
    SET_CURRENT_TARGET_BUILD(state, targetBuild) {
        state.currentTargetBuild = targetBuild;
        state.failedTestResultsLogsDetails = {};
    },
    SET_PRODUCTS(state, products) {
        state.products = products;
    },
    SET_CURRENT_FAILED_TEST_CASES(state, failedTestCases) {
        state.failedTestCases = failedTestCases;
    },
    SET_FAILED_TEST_RESULTS_LOGS_DETAILS(state, { testCaseId, testResults }) {
        state.failedTestResultsLogsDetails = Object.assign({},
                                                           state.failedTestResultsLogsDetails,
                                                           { [testCaseId]: testResults });
    },
};

const actions = {
    /**
     * Fetch target builds history and save it to state.
     * @return {Promise} result.
     */
    async fetchTargetBuildHistory({ commit }, { filters }) {
        try {
            const { target_build_history, total_count } = await api.targetBuilds.fetchHistory(filters);
            commit('SET_TARGET_BUILD_HISTORY', target_build_history);
            commit('SET_TARGET_BUILD_HISTORY_TOTAL_COUNT', total_count);
        } catch (error) {
            return Promise.reject('Error of target builds history fetching');
        }
    },
    /**
     * Fetch all branches and save it to state.
     * @return {Promise} result.
     */
    async fetchBranches({ commit, getters }) {
        if (getters.branches.length > 0) return;
        try {
            const branches = await api.targetBuilds.fetchBranches();
            commit('SET_BRANCHES', branches);
        } catch (error) {
            return Promise.reject('Error of branches list fetching');
        }
    },
    /**
     * Fetch target id by it name.
     * @return {Promise} result.
     */
    async fetchByName({}, name) {
        try {
            return await api.targetBuilds.fetchTargetByName(name);
        } catch (error) {
            return Promise.reject(`Error of fetching target ${name}`);
        }
    },
    /**
     * Fetch all products and save it to state.
     * @return {Promise} result.
     */
    async fetchProducts({ commit, getters }) {
        if (getters.products.length > 0) return;
        try {
            const products = await api.targetBuilds.fetchProducts();
            commit('SET_PRODUCTS', products);
        } catch (error) {
            return Promise.reject('Error of products list fetching');
        }
    },
    /**
     * Fetch target build and save it to state.
     * @return {Promise} result.
     */
    async fetchTargetBuild({ commit, state }, { targetBuildId }) {
        commit('SET_CURRENT_TARGET_BUILD', null);
        let targetBuild = state.testRunsTargetBuilds.find(item => item.id.toString() === targetBuildId.toString());
        try {
            const details = await api.targetBuilds.fetchTargetBuildDetails(targetBuildId);
            targetBuild = _.merge(targetBuild, details)
        } catch (error) {
            targetBuild.details = {};
        }
        commit('SET_CURRENT_TARGET_BUILD', targetBuild);
    },
    /**
     * Fetch mtr summary for target build and save it to state.
     * @return {Promise} result.
     */
    async fetchMtrSummary({ commit }, { targetBuildId }) {
        commit('SET_CURRENT_MTR_SUMMARY', null);
        try {
            const { mtr_summary, failed_tests } = await api.targetBuilds.fetchMtrSummaryForTargetBuild(targetBuildId);
            commit('SET_CURRENT_MTR_SUMMARY', mtr_summary);
            commit('SET_CURRENT_FAILED_TEST_CASES', failed_tests);
        } catch (error) {
            return Promise.reject('Error of mtr summary fetching');
        }
    },
    /**
     * Fetch all build triggers and save it to state.
     * @return {Promise} result.
     */
    async fetchBuildTypes({ commit, getters }) {
        if (getters.buildTypes.length > 0) return;
        try {
            const build_types = await api.targetBuilds.fetchBuildTypes();
            commit('SET_BUILD_TYPES', build_types);
        } catch (error) {
            return Promise.reject('Error of build triggers list fetching');
        }
    },
    /**
     * Fetch all ci services and save it to state.
     * @return {Promise} result.
     */
    async fetchCIServices({ commit, getters }) {
        if (getters.ciServices.length > 0) return;
        try {
            const ci_services = await api.targetBuilds.fetchCiServices();
            commit('SET_CI_SERVICES', ci_services);
        } catch (error) {
            return Promise.reject('Error of ci services list fetching');
        }
    },
    /**
     * Fetch all additional tools types and save it to state.
     * @return {Promise} result.
     */
    async fetchAdditionalTools({ commit, getters }) {
        if (getters.additionalTools.length > 0) return;
        try {
            const additional_tools = await api.targetBuilds.fetchAdditionalTools();
            commit('SET_ADDITIONAL_TOOLS', additional_tools);
        } catch (error) {
            return Promise.reject('Error of additional tools types list fetching');
        }
    },
    async fetchFailedTestResultsByTestCase({ commit }, { targetBuildId, testCaseId }) {
        try {
            const testResults = await api.testResults.fetchFailedTestResultsByTestCase(targetBuildId, testCaseId);
            commit('SET_FAILED_TEST_RESULTS_LOGS_DETAILS', { testCaseId, testResults });
        } catch (error) {
            return Promise.reject('Error of failed tests fetching');
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
